"use client";

import * as SliderPrimitive from "@radix-ui/react-slider";
import * as React from "react";
import { cn } from "../lib";

/**
 * Parent code often does `value={[n]}` — a new array every render. Radix may treat
 * that as a value change and re-fire `onValueChange` in a loop. Stabilize the
 * tuple when numeric values are unchanged.
 */
function useStableSliderControlValue(
	value: number[] | undefined,
): number[] | undefined {
	const valueTupleKey =
		value === undefined ? null : value.map((n) => String(n)).join("\0");
	// `value` intentionally omitted: `valueTupleKey` encodes it; including `value` would
	// recompute every render when parents pass a fresh `[]` with the same numbers.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	return React.useMemo(() => {
		if (value === undefined) {
			return undefined;
		}
		return value.slice();
	}, [valueTupleKey]);
}

export interface SliderProps
	extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
	trackClassName?: string;
	rangeClassName?: string;
	thumbClassName?: string;
	/** Merged onto the track; use for colors that must not depend on Tailwind JIT. */
	trackStyle?: React.CSSProperties;
	/** Merged by Radix with range sizing; safe for `backgroundColor` etc. */
	rangeStyle?: React.CSSProperties;
	/** Applied to each thumb; Radix keeps positioning on a wrapper span. */
	thumbStyle?: React.CSSProperties;
	/** Optional grip / marker content rendered inside each thumb. */
	thumbContent?: React.ReactNode;
	/** Values above this (up to `max`) are non-interactive; track segment above renders disabled. */
	interactiveMax?: number;
	disabledRangeClassName?: string;
}

const Slider = React.forwardRef<
	React.ComponentRef<typeof SliderPrimitive.Root>,
	SliderProps
>(
	(
		{
			className,
			defaultValue,
			value,
			trackClassName,
			rangeClassName,
			thumbClassName,
			trackStyle,
			rangeStyle,
			thumbStyle,
			thumbContent,
			orientation = "horizontal",
			min = 0,
			max = 100,
			interactiveMax,
			disabledRangeClassName,
			onValueChange,
			onValueCommit,
			...props
		},
		ref,
	) => {
		const stableValue = useStableSliderControlValue(value);
		const thumbCount = value?.length ?? defaultValue?.length ?? 1;
		const isVertical = orientation === "vertical";
		const effectiveInteractiveMax = interactiveMax ?? max;
		const disabledRangePercent =
			effectiveInteractiveMax < max
				? ((max - effectiveInteractiveMax) / (max - min)) * 100
				: 0;

		function clampValues(values: number[]): number[] {
			return values.map((entry) =>
				Math.min(entry, effectiveInteractiveMax),
			);
		}

		return (
			<SliderPrimitive.Root
				ref={ref}
				orientation={orientation}
				className={cn(
					isVertical
						? "relative flex min-h-0 w-full touch-none select-none flex-col items-center justify-center"
						: "relative flex w-full touch-none select-none items-center",
					className,
				)}
				defaultValue={defaultValue}
				value={value !== undefined ? stableValue : undefined}
				min={min}
				max={max}
				onValueChange={(values) => {
					onValueChange?.(clampValues(values));
				}}
				onValueCommit={(values) => {
					onValueCommit?.(clampValues(values));
				}}
				{...props}
			>
				<SliderPrimitive.Track
					style={trackStyle}
					className={cn(
						isVertical
							? "relative h-full w-1.5 shrink-0 grow overflow-hidden rounded-full"
							: "relative h-1.5 w-full grow overflow-hidden rounded-full",
						trackClassName ?? "bg-muted",
					)}
				>
					{disabledRangePercent > 0 ? (
						<div
							className={cn(
								"pointer-events-none absolute top-0 h-full rounded-r-full",
								isVertical
									? "bottom-0 left-0 w-full rounded-b-full rounded-r-none"
									: "right-0",
								disabledRangeClassName ??
									"bg-muted-foreground/20",
							)}
							style={
								isVertical
									? { height: `${disabledRangePercent}%` }
									: { width: `${disabledRangePercent}%` }
							}
							aria-hidden
						/>
					) : null}
					<SliderPrimitive.Range
						style={rangeStyle}
						className={cn(
							isVertical ? "absolute w-full" : "absolute h-full",
							"rounded-full",
							rangeClassName ?? "bg-primary",
						)}
					/>
				</SliderPrimitive.Track>
				{Array.from({ length: thumbCount }).map((_, i) => (
					<SliderPrimitive.Thumb
						key={`thumb-${i}`}
						style={thumbStyle}
						className={cn(
							"block size-5 rounded-full border-2 shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
							thumbClassName ?? "border-primary bg-background",
						)}
					>
						{thumbContent}
					</SliderPrimitive.Thumb>
				))}
			</SliderPrimitive.Root>
		);
	},
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
